import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Explore.css';
import publicMeals from '../../resources/publicMeals.json';
import { generateMealUrl } from '../../utils/urlHelpers';

const Explore = () => {
    const [meals, setMeals] = useState([]);
    const [filteredMeals, setFilteredMeals] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [selectedCuisine, setSelectedCuisine] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [mealsPerPage] = useState(12);

    // Get unique cuisines for filter dropdown
    const uniqueCuisines = ['all', ...Array.from(new Set(meals.map(meal => meal.cuisine).filter(Boolean)))].sort();

    useEffect(() => {
        setMeals(publicMeals);
        setFilteredMeals(publicMeals);
    }, []);

    useEffect(() => {
        let filtered = meals.filter(meal => {
            const matchesSearch = meal.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCuisine = selectedCuisine === 'all' || meal.cuisine === selectedCuisine;
            return matchesSearch && matchesCuisine;
        });

        // Sort meals
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'calories-low':
                    return parseFloat(a.total_calories) - parseFloat(b.total_calories);
                case 'calories-high':
                    return parseFloat(b.total_calories) - parseFloat(a.total_calories);
                case 'protein-high':
                    return parseFloat(b.total_protein) - parseFloat(a.total_protein);
                case 'cost-low':
                    return parseFloat(a.cost) - parseFloat(b.cost);
                case 'cost-high':
                    return parseFloat(b.cost) - parseFloat(a.cost);
                default:
                    return 0;
            }
        });

        setFilteredMeals(filtered);
        setCurrentPage(1); // Reset to first page when filtering/sorting changes
    }, [meals, searchTerm, sortBy, selectedCuisine]);

    // Pagination logic
    const indexOfLastMeal = currentPage * mealsPerPage;
    const indexOfFirstMeal = indexOfLastMeal - mealsPerPage;
    const currentMeals = filteredMeals.slice(indexOfFirstMeal, indexOfLastMeal);
    const totalPages = Math.ceil(filteredMeals.length / mealsPerPage);

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Generate page numbers for pagination
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) {
                    pageNumbers.push(i);
                }
                pageNumbers.push('...');
                pageNumbers.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pageNumbers.push(1);
                pageNumbers.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pageNumbers.push(i);
                }
            } else {
                pageNumbers.push(1);
                pageNumbers.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pageNumbers.push(i);
                }
                pageNumbers.push('...');
                pageNumbers.push(totalPages);
            }
        }

        return pageNumbers;
    };

    return (
        <>
            <Helmet>
                <title>Sample Collection - PrepWize | Discover Delicious Recipes</title>
                <meta name="description" content="Explore sample of our collection of delicious meals from around the world. Find recipes, nutritional information, and cooking instructions in our mobile app." />
                <meta name="keywords" content="meals, recipes, food, cooking, nutrition, calorie tracker, macro tracker" />
                <link rel="canonical" href="https://prepwize.ai/explore" />
            </Helmet>

            <div className="explore-container">
                <div className="explore-header">
                    <h1>Sample Meal Collection</h1>
                    <p>Discover delicious recipes from around the world. Get full recipes, ingredients, and cooking instructions in our mobile app.</p>
                    <div className="sample-notice">
                        <span className="sample-badge">Sample Collection</span>
                        <p>This is just a preview of our meal library. Download <a href="https://apps.apple.com/us/app/prepwize-ai-meal-planner/id6744729394" target="_blank" rel="noopener noreferrer" className="app-link">PrepWize</a> to access <strong>hundreds more recipes</strong> with complete nutritional data, cooking instructions, and cutting edge calorie tracking features.</p>
                    </div>
                </div>

                <div className="explore-controls">
                    <div className="search-section">
                        <input
                            type="text"
                            placeholder="Search meals..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck="false"
                        />
                    </div>

                    <div className="filter-section">
                        <select
                            value={selectedCuisine}
                            onChange={(e) => setSelectedCuisine(e.target.value)}
                            className="filter-select"
                        >
                            {uniqueCuisines.map(cuisine => (
                                <option key={cuisine} value={cuisine}>
                                    {cuisine === 'all' ? 'All Cuisines' : cuisine}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="sort-section">
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="sort-select"
                        >
                            <option value="name">Sort by Name</option>
                            <option value="calories-low">Calories (Low to High)</option>
                            <option value="calories-high">Calories (High to Low)</option>
                            <option value="protein-high">Protein (High to Low)</option>
                            <option value="cost-low">Cost (Low to High)</option>
                            <option value="cost-high">Cost (High to Low)</option>
                        </select>
                    </div>
                </div>

                <div className="meals-grid">
                    {currentMeals.map((meal) => (
                        <Link to={generateMealUrl(meal.name, meal.meal_id)} key={meal.meal_id} className="meal-card">
                            <div className="meal-image">
                                <img src={meal.image} alt={meal.name} />
                                {meal.cuisine && (
                                    <div className="meal-cuisine-badge">
                                        <span className="cuisine-icon">🌍</span>
                                        <span className="cuisine-text">{meal.cuisine}</span>
                                    </div>
                                )}
                            </div>
                            <div className="meal-info">
                                <h3>{meal.name}</h3>
                                <div className="meal-stats">
                                    <div className="stat">
                                        <span className="stat-label">Calories:</span>
                                        <span className="stat-value">{meal.total_calories}</span>
                                    </div>
                                    <div className="stat">
                                        <span className="stat-label">Protein:</span>
                                        <span className="stat-value">{meal.total_protein}g</span>
                                    </div>
                                    <div className="stat">
                                        <span className="stat-label">Cost:</span>
                                        <span className="stat-value">${meal.cost}</span>
                                    </div>
                                </div>
                                <div className="meal-cta">
                                    <span>View Recipe →</span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="pagination-container">
                        <div className="pagination-info">
                            Showing {indexOfFirstMeal + 1}-{Math.min(indexOfLastMeal, filteredMeals.length)} of {filteredMeals.length} sample meals
                        </div>
                        <div className="pagination">
                            <button
                                onClick={() => paginate(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="pagination-btn prev-btn"
                            >
                                ← Previous
                            </button>

                            <div className="page-numbers">
                                {getPageNumbers().map((number, index) => (
                                    <button
                                        key={index}
                                        onClick={() => typeof number === 'number' ? paginate(number) : null}
                                        className={`pagination-btn page-btn ${currentPage === number ? 'active' : ''} ${typeof number !== 'number' ? 'ellipsis' : ''}`}
                                        disabled={typeof number !== 'number'}
                                    >
                                        {number}
                                    </button>
                                ))}
                            </div>

                            <button
                                onClick={() => paginate(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="pagination-btn next-btn"
                            >
                                Next →
                            </button>
                        </div>
                    </div>
                )}

                {filteredMeals.length === 0 && (
                    <div className="no-results">
                        <h3>No meals found</h3>
                        <p>Try adjusting your search terms or filters.</p>
                    </div>
                )}

                {/* <div className="more-meals-cta">
                    <div className="cta-content">
                        <h3>Want More Meals?</h3>
                        <p>This is just a small sample of our extensive meal library. Download <a href="https://apps.apple.com/us/app/prepwize-ai-meal-planner/id6744729394" target="_blank" rel="noopener noreferrer" className="app-link">PrepWize</a> to unlock:</p>
                        <ul className="features-list">
                            <li>✨ <strong>500+ recipes</strong> with complete nutritional information</li>
                            <li>🍳 <strong>Step-by-step cooking instructions</strong> with photos</li>
                            <li>📊 <strong>Detailed ingredient lists</strong> with quantities</li>
                            <li>🎯 <strong>Personalized meal planning</strong> based on your preferences</li>
                            <li>📱 <strong>Smart shopping lists</strong> and meal prep guides</li>
                        </ul>
                        <div className="cta-buttons">
                            <a href="https://apps.apple.com/us/app/prepwize-ai-meal-planner/id6744729394" target="_blank" rel="noopener noreferrer" className="cta-button primary">Download PrepWize</a>
                        </div>
                    </div>
                </div> */}

                <div className="explore-footer">
                    <div className="app-cta">
                        <h3>Get Full Recipes & More!</h3>
                        <p>Download PrepWize to access complete recipes, ingredients, cooking instructions, and cutting edge calorie tracking features.</p>
                        <div className="cta-buttons">
                            <a href="https://apps.apple.com/us/app/prepwize-ai-meal-planner/id6744729394" target="_blank" rel="noopener noreferrer" className="cta-button primary">Download PrepWize</a>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Explore;
